import type { DraftRetainerPreviewParams } from '@common/types/retainerPreview';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import PDFViewer from '@/components/PdfViewer/PdfViewer';
import { fetchRetainerPreviewPdf } from '@/services/RetainerPreviewService';

interface Props {
    fileId: string;
    draftPreview: DraftRetainerPreviewParams;
    onClose(): void;
}

export default function DocumentPreviewModal({ fileId, draftPreview, onClose }: Props) {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const objectUrlRef = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const revokeObjectUrl = () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };

        async function loadPreview() {
            setIsLoading(true);
            setLoadFailed(false);
            revokeObjectUrl();
            setPdfUrl(null);

            try {
                const blob = await fetchRetainerPreviewPdf(fileId, draftPreview);
                if (cancelled) return;
                const objectUrl = URL.createObjectURL(blob);
                objectUrlRef.current = objectUrl;
                setPdfUrl(objectUrl);
            } catch {
                if (cancelled) return;
                setLoadFailed(true);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }

        loadPreview();

        return () => {
            cancelled = true;
            revokeObjectUrl();
        };
    }, [fileId, draftPreview, loadAttempt]); // oxlint-disable-line react-hooks/exhaustive-deps -- Effect is re-run when retry is clicked

    return (
        <Dialog
            open
            onClose={onClose}
            PaperProps={{ sx: { maxWidth: '70vw' } }}
            fullWidth
            scroll="paper"
        >
            <DialogTitle>Document Preview</DialogTitle>
            <DialogContent sx={{ height: '90vh', overflow: 'hidden' }}>
                <Stack sx={{ height: '100%', minHeight: '75%', overflow: 'hidden' }}>
                    {isLoading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                            <CircularProgress />
                        </Stack>
                    ) : loadFailed ? (
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            spacing={2}
                            sx={{ height: '100%' }}
                        >
                            <Button variant="outlined" onClick={() => setLoadAttempt((n) => n + 1)}>
                                Retry
                            </Button>
                        </Stack>
                    ) : (
                        <PDFViewer pdfUrl={pdfUrl ?? undefined} />
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
